import React, { useCallback, useEffect, useMemo, useState } from "react";
import { type ColumnDef, type PaginationState } from "@tanstack/react-table";
import { Check, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TradePositionSideBadge } from "@/components/trade-position-side-badge";
import {
  exportReportStrategyResearchIndicator,
  getReportStrategyResearchSim,
} from "../../../services/strategyRdApi";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import {
  loadStrategyRdPineExportInfo,
  saveStrategyRdPineExportInfo,
  strategyRdPineChartTimeframeHint,
  strategyRdPineScriptTitle,
  strategyRdPineScriptsForDisplay,
  type StrategyRdPineExportInfo,
} from "../../../utils/strategyRdPineExport";
import {
  formatStrategyRdMoney,
  formatStrategyRdOutcomeCell,
  formatStrategyRdPrice,
  formatStrategyRdQty,
  strategyRdOutcomeLabel,
} from "../../../utils/strategyRd";
import type {
  StrategyRdApplyOverview,
  StrategyRdFill,
  StrategyRdPineScript,
} from "../../../types/strategyRd";
import {
  PNL_NEGATIVE_COLOR,
  PNL_POSITIVE_COLOR,
} from "../Statistics/components/statsSignedPnlBarUtils";
import { StatisticsCardHeadline } from "../Statistics/components/statisticsCardHeadline";
import { TradeLogSortableCell } from "../TradeLog/components/tradeLogSortableCell";
import { StrategyRdOverviewStats } from "../StrategyRd/StrategyRdOverviewStats";
import { StrategyRdBacktestWarnings } from "../StrategyRd/StrategyRdBacktestWarnings";
import { StrategyRdRobustnessPanel } from "../StrategyRd/StrategyRdRobustnessPanel";
import { strategyRdMetaClassName, strategyRdSectionTitleClassName } from "../StrategyRd/strategyRdCardShell";
import { formatTradeDateTimeForDisplay } from "../../../utils/displayDateTime";
import { useOpenStrategyReport } from "../Workspace/useOpenStrategyReport";

const PAGE_SIZE = 50;

type FillRow = StrategyRdFill & { key: string };

export type StrategyRdResearchSimPanelProps = {
  strategyId: string;
  hasResearchSim?: boolean;
  fillCount?: number | null;
  timeZone: string;
  onStrategyRefresh?: () => void;
};

/**
 * Strategy-owned Research sim cohort (Confirm v2).
 * Strategy Lab canvas (strategy → cohort → report → optional account) waits until
 * report generate can consume research_sim — this panel is the interim surface.
 */
export function StrategyRdResearchSimPanel({
  strategyId,
  hasResearchSim,
  fillCount,
  timeZone,
  onStrategyRefresh,
}: StrategyRdResearchSimPanelProps): JSX.Element | null {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pineInfo, setPineInfo] = useState<StrategyRdPineExportInfo | null>(() =>
    loadStrategyRdPineExportInfo(strategyId)
  );
  useEffect(() => {
    setPineInfo(loadStrategyRdPineExportInfo(strategyId));
  }, [strategyId]);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<StrategyRdApplyOverview | null>(null);
  const [fills, setFills] = useState<StrategyRdFill[]>([]);
  const [hypothesisId, setHypothesisId] = useState<string | null>(null);
  const { openReport, opening: openingReport } = useOpenStrategyReport();
  const [scriptsBySide, setScriptsBySide] = useState<Record<string, string> | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [robustnessRunToken, setRobustnessRunToken] = useState(0);
  /** From the saved robustness result when this session didn't hand one over. */
  const [robustnessTvSymbol, setRobustnessTvSymbol] = useState<string | null>(null);
  const onRobustnessResult = useCallback(
    (rb: { parity?: { tv_symbol?: string | null } | null } | null) =>
      setRobustnessTvSymbol(rb?.parity?.tv_symbol ?? null),
    []
  );

  useEffect(() => {
    if (!hasResearchSim || !strategyId.trim()) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void getReportStrategyResearchSim(strategyId, controller.signal)
      .then((resp) => {
        if (controller.signal.aborted) return;
        setOverview(resp.research_sim.overview ?? null);
        setFills(resp.research_sim.fills ?? []);
        setHypothesisId(resp.research_sim.hypothesis_id?.trim() || null);
        setScriptsBySide(resp.research_sim.pinescript_by_side ?? null);
        setLoaded(true);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : "Could not load Research sim.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [hasResearchSim, strategyId]);

  const scripts = useMemo(
    () =>
      strategyRdPineScriptsForDisplay({
        bySide: scriptsBySide,
        confirmScripts: pineInfo?.scripts,
      }),
    [pineInfo?.scripts, scriptsBySide]
  );

  const tableData: FillRow[] = useMemo(
    () =>
      fills.map((f, i) => ({
        ...f,
        key: `${f.bar_time || f.entry_time || "fill"}-${i}`,
      })),
    [fills]
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [fills.length]);

  const columns: ColumnDef<FillRow, unknown>[] = useMemo(
    () => [
      {
        id: "entry",
        accessorFn: (row) => row.entry_time || row.bar_time || "",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Entry" />
        ),
        cell: ({ row }) => (
          <TradeLogSortableCell>
            {formatTradeDateTimeForDisplay(
              row.original.entry_time || row.original.bar_time || "",
              timeZone
            )}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "side",
        accessorFn: (row) => row.direction || "",
        header: "Type",
        enableSorting: false,
        cell: ({ row }) => {
          const d = String(row.original.direction || "").toLowerCase();
          if (d === "short") return <TradePositionSideBadge side="short" />;
          if (d === "long") return <TradePositionSideBadge side="long" />;
          return <TradeLogSortableCell>—</TradeLogSortableCell>;
        },
      },
      {
        id: "entryPrice",
        accessorFn: (row) => row.entry_price ?? null,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Entry Price" />
        ),
        cell: ({ row }) => (
          <TradeLogSortableCell className="font-medium">
            {formatStrategyRdPrice(row.original.entry_price)}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "stop",
        accessorFn: (row) => row.stop ?? null,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Stop" />,
        cell: ({ row }) => (
          <TradeLogSortableCell className="font-medium">
            {formatStrategyRdPrice(row.original.stop)}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "target",
        accessorFn: (row) => row.target ?? null,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Target" />,
        cell: ({ row }) => (
          <TradeLogSortableCell className="font-medium">
            {formatStrategyRdPrice(row.original.target)}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "exitPrice",
        accessorFn: (row) => row.exit_price ?? null,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Exit Price" />,
        cell: ({ row }) => {
          const r = row.original.result;
          const exit = row.original.exit_price;
          return (
            <TradeLogSortableCell className="font-medium">
              {exit != null && r !== "open" && r !== "skipped" ? formatStrategyRdPrice(exit) : "—"}
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "outcome",
        accessorFn: (row) => row.result || "",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Outcome" />
        ),
        cell: ({ row }) => {
          const result = row.original.result;
          const color =
            result === "target"
              ? PNL_POSITIVE_COLOR
              : result === "stop" || result === "liquidated"
                ? PNL_NEGATIVE_COLOR
                : undefined;
          return (
            <TradeLogSortableCell className="font-medium">
              <span style={color ? { color } : undefined}>
                {formatStrategyRdOutcomeCell({
                  result: result as never,
                  barsToEvent: row.original.bars_to_event,
                }) || strategyRdOutcomeLabel(result as never)}
              </span>
            </TradeLogSortableCell>
          );
        },
      },
      {
        id: "qty",
        accessorFn: (row) => row.qty ?? null,
        header: ({ column }) => <DataTableColumnHeader column={column} title="Qty" />,
        cell: ({ row }) => (
          <TradeLogSortableCell className="font-medium">
            {formatStrategyRdQty(row.original.qty)}
          </TradeLogSortableCell>
        ),
      },
      {
        id: "pnl",
        accessorFn: (row) => row.pnl ?? null,
        header: ({ column }) => <DataTableColumnHeader column={column} title="PnL $" />,
        cell: ({ row }) => {
          const pnl = row.original.pnl;
          const text = formatStrategyRdMoney(pnl);
          const color =
            pnl != null && pnl > 0
              ? PNL_POSITIVE_COLOR
              : pnl != null && pnl < 0
                ? PNL_NEGATIVE_COLOR
                : undefined;
          return (
            <TradeLogSortableCell className="font-medium">
              <span style={color ? { color } : undefined}>{text ?? "—"}</span>
            </TradeLogSortableCell>
          );
        },
      },
    ],
    [timeZone]
  );

  const onExportIndicator = async () => {
    setExporting(true);
    try {
      const resp = await exportReportStrategyResearchIndicator(strategyId);
      if (!resp.indicator_pine_source.trim()) {
        toast.info("No indicator script returned");
        return;
      }
      await navigator.clipboard.writeText(resp.indicator_pine_source);
      const info: StrategyRdPineExportInfo = {
        pineNotes: resp.pine_notes,
        chartTimeframe: resp.chart_timeframe,
      };
      if (info.pineNotes.length > 0 || info.chartTimeframe) {
        // Keep the strategy scripts / robustness handed over by Confirm.
        const merged = { ...pineInfo, ...info };
        setPineInfo(merged);
        saveStrategyRdPineExportInfo(strategyId, merged);
      }
      toast.success("Copied Research indicator Pine", {
        description: strategyRdPineChartTimeframeHint(resp.chart_timeframe) ?? undefined,
      });
      onStrategyRefresh?.();
    } catch (err) {
      presentMutationError(err, "Could not export indicator");
    } finally {
      setExporting(false);
    }
  };

  if (!hasResearchSim) return null;

  const countLabel =
    fillCount != null && Number.isFinite(fillCount)
      ? Math.round(fillCount)
      : fills.length;

  return (
    <div
      className="flex flex-col gap-4 rounded-[6px] border border-border bg-background p-4"
      data-strategy-research-sim-panel=""
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className={strategyRdSectionTitleClassName}>Research sim</h3>
          <p className={strategyRdMetaClassName}>
            Strategy-owned cohort from Research Apply — not journal trades.
            {countLabel > 0 ? ` · ${countLabel} fill${countLabel === 1 ? "" : "s"}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            disabled={exporting || loading}
            onClick={() => void onExportIndicator()}
          >
            {exporting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span className="normal-case tracking-normal">Export as indicator</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            disabled={!hypothesisId || openingReport}
            title={hypothesisId ? "Create a report on this strategy's backtest trades" : "Run the backtest in the Strategy Lab first"}
            onClick={() => hypothesisId && void openReport(strategyId, `backtest:${hypothesisId}`)}
          >
            {openingReport ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span className="normal-case tracking-normal">Report on these trades</span>
          </Button>
        </div>
      </div>

      {scripts.length > 0 ? (
        <div className="flex flex-col gap-3" data-strategy-rd-pine-scripts="">
          {scripts.length > 1 ? (
            <p className={strategyRdMetaClassName}>
              A TradingView strategy can't hold a long and a short at once, so longs and shorts are
              two scripts. Add both to the chart.
            </p>
          ) : null}
          {scripts.map((s) => (
            <PineScriptCopyBox key={s.side} script={s} />
          ))}
          <p className={strategyRdMetaClassName} data-strategy-rd-paste-note="">
            Paste into TradingView on {pineInfo?.tvSymbol || robustnessTvSymbol || "the same symbol"} at
            the chart timeframe
            {pineInfo?.chartTimeframe ? ` (${pineInfo.chartTimeframe})` : ""}. The Self-check table
            (bottom right) compares its trades with this backtest. On forex and gold some differences
            are normal — see the robustness range.
          </p>
        </div>
      ) : null}

      {pineInfo && (pineInfo.chartTimeframe || pineInfo.pineNotes.length > 0) ? (
        <div className="flex flex-col gap-1.5 rounded-[6px] border border-border bg-muted/30 px-3 py-2.5">
          <p className="text-sm font-medium text-foreground">
            TradingView
            {pineInfo.chartTimeframe
              ? ` · ${strategyRdPineChartTimeframeHint(pineInfo.chartTimeframe)} The script stops on any other timeframe.`
              : null}
          </p>
          {pineInfo.pineNotes.length > 0 ? (
            <ul className={`${strategyRdMetaClassName} list-disc space-y-0.5 pl-4`}>
              {pineInfo.pineNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {hypothesisId ? (
        <StrategyRdRobustnessPanel
          hypothesisId={hypothesisId}
          loadFromHypothesis
          initial={pineInfo?.robustness ?? null}
          timeZone={timeZone}
          runToken={robustnessRunToken}
          onResultChange={onRobustnessResult}
        />
      ) : null}

      {loading && !loaded ? (
        <div className="flex items-center gap-2 py-6">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          <span className={strategyRdMetaClassName}>Loading Research sim…</span>
        </div>
      ) : error ? (
        <p className={strategyRdMetaClassName}>{error}</p>
      ) : (
        <Tabs defaultValue="statistics" className="w-full min-w-0">
          <TabsList className="min-w-max">
            <TabsTrigger value="statistics">Statistics</TabsTrigger>
            <TabsTrigger value="trades">Trades</TabsTrigger>
          </TabsList>
          <TabsContent value="statistics" className="mt-4 flex flex-col gap-4">
            <StrategyRdBacktestWarnings
              warnings={overview?.warnings}
              onCheckRobustness={
                hypothesisId ? () => setRobustnessRunToken((n) => n + 1) : undefined
              }
            />
            <StrategyRdOverviewStats overview={overview} fills={fills} />
          </TabsContent>
          <TabsContent value="trades" className="mt-4">
            <div className="rounded-[6px] border border-border bg-background p-4">
              <div className="mb-4">
                <StatisticsCardHeadline>Closed fills</StatisticsCardHeadline>
              </div>
              <DataTable
                columns={columns}
                data={tableData}
                getRowId={(row) => row.key}
                pagination={pagination}
                onPaginationChange={setPagination}
                showPagination
                tableClassName="dark:!bg-sidebar"
                emptyMessage="No closed Research sim fills."
              />
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

/** One TradingView script with a copy button ("Longs script" / "Shorts script" / "Script"). */
export function PineScriptCopyBox({ script }: { script: StrategyRdPineScript }): JSX.Element {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);
  const title = strategyRdPineScriptTitle(script.side);
  return (
    <div
      className="flex flex-col gap-2 rounded-[6px] border border-border bg-muted/30 p-3"
      data-strategy-rd-pine-script={script.side}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-font="primary"
          onClick={() => {
            void navigator.clipboard
              .writeText(script.script)
              .then(() => setCopied(true))
              .catch(() => toast.error("Could not copy — select the script and copy it by hand."));
          }}
        >
          {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
          <span className="normal-case tracking-normal">{copied ? "Copied" : "Copy"}</span>
        </Button>
      </div>
      <pre className="max-h-48 overflow-auto rounded-[4px] bg-background p-2 font-mono text-[11px] leading-snug text-muted-foreground">
        {script.script}
      </pre>
    </div>
  );
}
